// ==========================================================================
// TOAST & FEEDBACK NOTIFICATION SYSTEM
// Modern, sleek, non-blocking toast notifications & confirmation modals
// ==========================================================================
import { escapeHtml } from './sanitize.js';

let container = null;

function getToastContainer() {
  if (!container || !document.body.contains(container)) {
    container = document.getElementById('toast-notification-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'toast-notification-container';
      container.className = 'toast-container';
      document.body.appendChild(container);
    }
  }
  return container;
}

const TYPE_ICONS = {
  success: '✅',
  error: '❌',
  warning: '⚠️',
  info: 'ℹ️'
};

/**
 * Show a toast notification
 * @param {Object|string} options - Options object or message string
 * @param {string} [type='info'] - Type if options is string ('success', 'error', 'warning', 'info')
 * @param {string} [title=''] - Optional title
 * @param {number} [duration=3500] - Duration in ms (0 for persistent)
 */
export function showToast(options, type = 'info', title = '', duration = 3500) {
  let opts = {};
  if (typeof options === 'string') {
    opts = { message: options, type, title, duration };
  } else if (options && typeof options === 'object') {
    opts = Object.assign({ type: 'info', duration: 3500, title: '' }, options);
  } else {
    opts = { message: String(options || ''), type: 'info', duration: 3500, title: '' };
  }

  const toastContainer = getToastContainer();
  const toast = document.createElement('div');
  const toastType = opts.type || 'info';
  toast.className = `toast-box toast-${toastType}`;

  const icon = opts.icon || TYPE_ICONS[toastType] || 'ℹ️';
  const hasTitle = Boolean(opts.title);

  toast.innerHTML = `
    <div class="toast-main-content">
      <div class="toast-icon-badge">${icon}</div>
      <div class="toast-text-area">
        ${hasTitle ? `<div class="toast-title">${escapeHtml(opts.title)}</div>` : ''}
        <div class="toast-message">${opts.message}</div>
      </div>
      <button class="toast-close-btn" aria-label="Close" type="button">&times;</button>
    </div>
    ${opts.duration > 0 ? `<div class="toast-progress-bar" style="animation-duration: ${opts.duration}ms;"></div>` : ''}
  `;

  toastContainer.appendChild(toast);

  let timer = null;
  let isClosing = false;
  const closeToast = () => {
    if (isClosing) return;
    isClosing = true;
    if (timer) clearTimeout(timer);
    toast.classList.remove('active');
    setTimeout(() => {
      if (toast.parentNode) toast.remove();
    }, 350);
  };

  const closeBtn = toast.querySelector('.toast-close-btn');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeToast);
  }

  requestAnimationFrame(() => {
    toast.classList.add('active');
  });

  if (opts.duration > 0) {
    timer = setTimeout(closeToast, opts.duration);

    toast.addEventListener('mouseenter', () => {
      const bar = toast.querySelector('.toast-progress-bar');
      if (bar) bar.style.animationPlayState = 'paused';
      if (timer) clearTimeout(timer);
    });

    toast.addEventListener('mouseleave', () => {
      const bar = toast.querySelector('.toast-progress-bar');
      if (bar) bar.style.animationPlayState = 'running';
      timer = setTimeout(closeToast, 1500);
    });
  }

  return { close: closeToast, element: toast };
}

// Convenient helper methods
export const toast = {
  success(message, title = 'สำเร็จ') {
    return showToast({ message, title, type: 'success', duration: 3500 });
  },
  error(message, title = 'เกิดข้อผิดพลาด') {
    return showToast({ message, title, type: 'error', duration: 5000 });
  },
  warning(message, title = 'แจ้งเตือน') {
    return showToast({ message, title, type: 'warning', duration: 4500 });
  },
  info(message, title = 'แจ้งข้อมูล') {
    return showToast({ message, title, type: 'info', duration: 3500 });
  }
};

/**
 * Modern Promise-based confirmation modal
 * @param {Object|string} options
 * @returns {Promise<boolean>}
 */
export function showConfirm(options = {}) {
  const opts = typeof options === 'string' ? { message: options } : options;
  const {
    title = 'ยืนยันการดำเนินการ',
    message = 'คุณแน่ใจหรือไม่ว่าต้องการดำเนินการนี้?',
    confirmText = 'ยืนยัน',
    cancelText = 'ยกเลิก',
    isDanger = false,
    icon = isDanger ? '⚠️' : '❓'
  } = opts;

  return new Promise((resolve) => {
    let overlay = document.getElementById('global-confirm-dialog-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'global-confirm-dialog-overlay';
      overlay.className = 'confirm-dialog-overlay';
      document.body.appendChild(overlay);
    }

    overlay.innerHTML = `
      <div class="confirm-dialog-card">
        <div class="confirm-dialog-icon ${isDanger ? 'danger' : 'primary'}">${icon}</div>
        <h3 class="confirm-dialog-title">${escapeHtml(title)}</h3>
        <p class="confirm-dialog-message">${escapeHtml(message)}</p>
        <div class="confirm-dialog-actions">
          <button type="button" class="btn btn-secondary confirm-cancel-btn">${escapeHtml(cancelText)}</button>
          <button type="button" class="btn ${isDanger ? 'btn-danger' : 'btn-primary'} confirm-ok-btn">${escapeHtml(confirmText)}</button>
        </div>
      </div>
    `;

    overlay.style.display = 'flex';
    requestAnimationFrame(() => {
      overlay.classList.add('active');
    });

    const cleanup = (result) => {
      overlay.classList.remove('active');
      setTimeout(() => {
        overlay.style.display = 'none';
        overlay.innerHTML = '';
        resolve(result);
      }, 250);
    };

    const okBtn = overlay.querySelector('.confirm-ok-btn');
    const cancelBtn = overlay.querySelector('.confirm-cancel-btn');
    if (okBtn) okBtn.addEventListener('click', () => cleanup(true));
    if (cancelBtn) cancelBtn.addEventListener('click', () => cleanup(false));

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) cleanup(false);
    }, { once: true });
  });
}

export function showToastNotification(title, messageHtml) {
  showToast({ title, message: messageHtml, type: 'success', duration: 5000 });
}

// Bind to window for global access
if (typeof window !== 'undefined') {
  window.showToast = showToast;
  window.toast = toast;
  window.showConfirm = showConfirm;
  window.showToastNotification = showToastNotification;
}
